import { useQueryClient } from '@tanstack/react-query';
import { useGetProcesses } from 'gcs-common/clients/api/entities/processes/processes-api';
import { getGetProcessesQueryKey } from 'gcs-common/clients/api/entities/processes/processes-api-helpers';
import { getSelectedProcess } from 'gcs-common/clients/api/entities/processes/processes-api-selectors';
import Alert from 'gcs-common/components/Alert/Alert';
import { LEFT_BAR_TABS } from 'gcs-common/constants/uiFlags';
import FEATURE_FLAGS from 'gcs-common/helper/featureFlagsHelper';
import { opMaintenanceMessage } from 'gcs-common/helper/opMaintenanceHelper';
import { getChannelOPUserId, getPrimaryHouseForChannel } from 'gcs-common/slices/channel/channelSelectors';
import { getSelectedChannelId } from 'gcs-common/slices/channels/channelsSelectors';
import { getHasFeatureFlag, getIsAgent } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { getIsOnline } from 'gcs-common/slices/offline/offlineSelectors';
import { FETCH_PROCESSES_ERRORS } from 'gcs-common/slices/processes/processesConstants';
import { getFilter, getProcessSearchInput } from 'gcs-common/slices/processes/processesSelectors';
import { processSelected, productSelected, resetProcessFilter } from 'gcs-common/slices/processes/processesSlice';
import { setLeftBarTab } from 'gcs-common/slices/uiState/uiStateSlice';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../Button/Button';
import ProcessDetail from '../ProcessDetail/ProcessDetail';
import ProcessList from './ProcessList/ProcessList';
import ProcessSearch from './ProcessSearch/ProcessSearch';
import RenderHeader from './RenderHeader/RenderHeader';
import SuggestionList from './SuggestionList/SuggestionList';
import styles from './styles.module.scss';

const ProcessSelect = () => {
  const isAgent: boolean = useSelector(getIsAgent);
  const isOnline: boolean = useSelector(getIsOnline);
  const selectedChannelId: string = useSelector(getSelectedChannelId);
  const searchInput = useSelector(getProcessSearchInput);
  const channelOpUserId: string = useSelector(getChannelOPUserId(selectedChannelId));
  const filter = useSelector(getFilter);
  const { name: queryString, type: queryType } = filter || {};
  const houseId: string = useSelector(getPrimaryHouseForChannel(selectedChannelId));
  const isOpMaintenanceEnabled: boolean = useSelector(getHasFeatureFlag(FEATURE_FLAGS.OP_MAINTENANCE));

  const { error } = useGetProcesses({
    query: {
      query_type: queryType,
      query_string: queryString,
      user_id: isAgent ? channelOpUserId : undefined,
      house_id: houseId,
    },
  });
  const selectedProcess = getSelectedProcess();

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  useEffect(() => {
    dispatch(processSelected({ selectedProcessNumber: undefined }));
    dispatch(productSelected({ selectedProductNumber: undefined }));
    dispatch(resetProcessFilter());
  }, [dispatch, selectedChannelId]);

  const refetchProcesses = useCallback(() => {
    queryClient
      .invalidateQueries({
        queryKey: getGetProcessesQueryKey({
          query_type: queryType,
          query_string: queryString,
          user_id: isAgent ? channelOpUserId : undefined,
          house_id: houseId,
        }),
      })
      .then(() => {})
      .catch(() => {});
  }, [channelOpUserId, houseId, isAgent, queryClient, queryString, queryType]);

  const showProfile = useCallback(() => {
    dispatch(setLeftBarTab({ leftBarTab: LEFT_BAR_TABS.PROFILE }));
  }, [dispatch]);

  const errorMessageComponent = useMemo(() => {
    if (!error) return undefined;
    const { message: errorMessage, name: errorCode } = error;

    if (!isOnline) {
      return <div className={styles.errorMessage}>Warten auf Netzwerk</div>;
    }

    if (isOpMaintenanceEnabled) {
      return (
        <div className={styles.errorMessage}>
          <Alert type="info">
            <p className={styles.errorMessageText}>{opMaintenanceMessage}</p>
          </Alert>
        </div>
      );
    }

    if (!isAgent) {
      if (
        errorCode === FETCH_PROCESSES_ERRORS.NO_OP_ACCOUNT ||
        errorCode === FETCH_PROCESSES_ERRORS.MISSING_CREDENTIALS ||
        errorCode === FETCH_PROCESSES_ERRORS.SEARCH_DISABLED
      ) {
        return (
          <div className={styles.errorMessage} data-testid="process-error-dialog-craftsman">
            <p>
              <Alert type="info">
                <p className={styles.errorMessageText}>{errorMessage}</p>
              </Alert>
            </p>
            <p>Bitte prüfe &quot;Einstellungen/Verknüpfungen bearbeiten&quot;.</p>
            <Button className={styles.errorButton} onClick={showProfile}>
              Einstellungen öffnen
            </Button>

            <Button className={styles.errorButton} onClick={refetchProcesses}>
              Aktualisieren
            </Button>
          </div>
        );
      }
    }

    return (
      <div className={styles.errorMessage} data-testid="process-error-dialog-agents">
        <Alert type="info">
          <p className={styles.errorMessageText}>{errorMessage}</p>
        </Alert>
      </div>
    );
  }, [error, isOnline, isOpMaintenanceEnabled, isAgent, showProfile, refetchProcesses]);

  if (!selectedChannelId) return null;

  return (
    <div className={styles.processWrapper}>
      {selectedProcess ? (
        <ProcessDetail process={selectedProcess} />
      ) : (
        <>
          <RenderHeader />
          {!errorMessageComponent && (
            <div className={styles.searchWrapper}>
              <ProcessSearch />
            </div>
          )}
          {!errorMessageComponent && (searchInput.length > 0 ? <SuggestionList /> : <ProcessList />)}
          {errorMessageComponent}
        </>
      )}
    </div>
  );
};

export default ProcessSelect;
